import React, { createContext } from 'react';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import { bemHelper } from '../helpers';
import getGlobalProps from '../helpers/constants/global-props';

export interface ContextType {
  component?: React.ComponentClass;
  props?: any;
  // eslint-disable-next-line @typescript-eslint/ban-types
  showModal: (component: Function, props: any) => void;
  // eslint-disable-next-line @typescript-eslint/ban-types
  openModal: (component: Function, props: any) => void;
  hideModal: () => void;
  closeModal: () => void;
}

const globalProps = getGlobalProps({ inkSass: true });

const ModalContext = createContext<ContextType>({
  component: undefined,
  props: {},
  showModal: () => {},
  openModal: () => {},
  hideModal: () => {},
  closeModal: () => {},
});

const ModalConsumer = ModalContext.Consumer;

const withModalContext = Component => props => (
  <ModalConsumer>
    {({ showModal, hideModal, openModal, closeModal }) => (
      <Component {...props} showModal={showModal} openModal={openModal} hideModal={hideModal} closeModal={closeModal} />
    )}
  </ModalConsumer>
);

function defineClassName(component) {
  const b = bemHelper();

  return b('ink-modal', {
    open: Boolean(component),
  });
}

function handleBackdropClick({ preventBackdropClose, clickBackdropCallback }, hideModal) {
  if (!preventBackdropClose) {
    if (typeof clickBackdropCallback === 'function') {
      clickBackdropCallback();
    }
    hideModal();
  }
}

function onKeyDown({ key }, { preventBackdropClose, escKeyCallback }, hideModal) {
  if (!preventBackdropClose && key === 'Escape') {
    if (typeof escKeyCallback === 'function') {
      escKeyCallback();
    }
    hideModal();
  }
}

class ModalRoot extends React.Component<any, ContextType> {
  ref: React.RefObject<any>;

  constructor(props) {
    super(props);

    this.ref = React.createRef();
  }

  componentDidUpdate() {
    this.ref.current.focus();
  }

  render() {
    const transitionTimeout = 130;

    return (
      <ModalConsumer>
        {({ component: Component, props, hideModal }) => (
          <div
            className={defineClassName(Component)}
            onKeyDown={e => onKeyDown(e, props, hideModal)}
            onClick={() => handleBackdropClick(props, hideModal)}
            role="button"
            tabIndex={0}
            ref={this.ref}
            {...globalProps}
          >
            <TransitionGroup>
              {Component ? (
                <CSSTransition
                  classNames={{
                    enter: 'ink-modal--content__enter',
                    enterActive: 'ink-modal--content__enter-active',
                    exit: 'ink-modal--content__exit',
                    exitActive: 'ink-modal--content__exit-active',
                  }}
                  timeout={transitionTimeout}
                >
                  {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
                  <div className="ink-modal--body" onClick={e => e.stopPropagation()} role="button" tabIndex={0}>
                    <Component {...props} hideModal={hideModal} />
                  </div>
                </CSSTransition>
              ) : null}
            </TransitionGroup>
          </div>
        )}
      </ModalConsumer>
    );
  }
}

class ModalProvider extends React.Component<any, ContextType> {
  constructor(props) {
    super(props);

    this.showModal = this.showModal.bind(this);
    this.openModal = this.openModal.bind(this);
    this.hideModal = this.hideModal.bind(this);
    this.closeModal = this.closeModal.bind(this);

    this.state = {
      component: undefined,
      props: {},
      showModal: this.showModal,
      openModal: this.openModal,
      hideModal: this.hideModal,
      closeModal: this.hideModal,
    };
  }

  showModal(component, props = {}) {
    this.setState({
      component,
      props,
    });
  }

  openModal(component, props = {}) {
    this.setState({
      component,
      props,
    });
  }

  hideModal() {
    this.setState({
      component: undefined,
      props: {},
    });
  }

  closeModal() {
    this.setState({
      component: undefined,
      props: {},
    });
  }

  render() {
    return (
      <ModalContext.Provider value={this.state}>
        <ModalRoot />
        {this.props.children}
      </ModalContext.Provider>
    );
  }
}

export { ModalContext, ModalConsumer, withModalContext, ModalRoot, ModalProvider };
